/**
 * ReportBlockTile — shared chart renderer for the AI Report Builder.
 *
 * Used by both ``AIReportBuilderPage`` (in the editing canvas) and
 * ``PinnedReportsSection`` (on the home + LEI dashboards). Keeps
 * rendering consistent across surfaces: a pinned chart looks exactly
 * the same as the one you saved.
 *
 * A block is ``{id, query_spec, chart_spec, data}``. Chart-spec drives
 * EVERYTHING: chart_type, x_field, y_fields[], annotations (target
 * line / trend line), format ({y: percent|currency|number}), legend.
 */
import React from 'react';
import { TrendingUp, AlertTriangle, Sparkles, Search } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer,
  Legend, ReferenceLine,
} from 'recharts';

export const PALETTE = ['#394fd4', '#a855f7', '#10b981', '#f59e0b', '#ef4444', '#06b6d4'];

export function formatValue(v, fmt) {
  if (v === null || v === undefined) return '—';
  if (typeof v !== 'number') return String(v);
  if (fmt === 'percent') {
    const pct = v <= 1.0001 ? v * 100 : v;
    return `${pct.toFixed(1)}%`;
  }
  if (fmt === 'currency') return `$${v.toLocaleString()}`;
  return v.toLocaleString();
}

function pickXField(block) {
  // Defensive: when the spec says ``organization_id`` but the row
  // also carries ``organization_name`` (backend auto-enrichment),
  // prefer the human-readable name. Means old saved specs and any
  // LLM regressions still render nicely.
  const xRaw = block?.chart_spec?.x_field;
  const rows = block?.data?.rows || [];
  if (xRaw === 'organization_id' && rows[0]?.organization_name) {
    return 'organization_name';
  }
  return xRaw;
}

function ensureYFields(block) {
  const ys = block?.chart_spec?.y_fields;
  if (Array.isArray(ys) && ys.length) return ys;
  const rows = block?.data?.rows || [];
  if (!rows.length) return [];
  const x = block?.chart_spec?.x_field;
  const numKeys = Object.keys(rows[0]).filter(
    (k) => k !== x && typeof rows[0][k] === 'number'
  );
  return numKeys.slice(0, 1).map((k, i) => ({
    field: k, label: k, color: PALETTE[i % PALETTE.length],
  }));
}

function calcTrend(rows, xKey, yKey) {
  if (!rows || rows.length < 2) return null;
  const xs = rows.map((_, i) => i);
  const ys = rows.map((r) => Number(r[yKey]) || 0);
  const n = xs.length;
  const sumX = xs.reduce((a, b) => a + b, 0);
  const sumY = ys.reduce((a, b) => a + b, 0);
  const sumXY = xs.reduce((a, b, i) => a + b * ys[i], 0);
  const sumXX = xs.reduce((a, b) => a + b * b, 0);
  const denom = n * sumXX - sumX * sumX;
  if (!denom) return null;
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return rows.map((r, i) => ({ ...r, __trend: slope * i + intercept }));
}

function BigNumberBlock({ block }) {
  const rows = block?.data?.rows || [];
  const single = block?.data?.single_value;
  let value;
  let label = (block?.chart_spec?.y_label || block?.chart_spec?.x_label || '');
  if (single && rows[0]) {
    value = rows[0].count;
    label = label || 'Total';
  } else if (rows[0]) {
    const yField = block?.chart_spec?.y_fields?.[0]?.field
      || Object.keys(rows[0]).find((k) => typeof rows[0][k] === 'number');
    value = rows[0][yField];
    label = label || block?.chart_spec?.y_fields?.[0]?.label || yField;
  } else {
    value = 0;
  }
  const fmt = block?.chart_spec?.format?.y;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 flex flex-col gap-2"
      data-testid="report-block-big-number">
      <div className="text-5xl font-semibold text-slate-900 tracking-tight">
        {formatValue(value, fmt)}
      </div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
}

function TableBlock({ block }) {
  const rows = block?.data?.rows || [];
  if (!rows.length) {
    return <div className="text-sm text-slate-500 italic px-1">No rows returned.</div>;
  }
  // Hide the raw ``organization_id`` column when we have the
  // human-readable ``organization_name`` alongside it — keeps tables
  // tidy without losing the underlying join key from the row data.
  const allCols = Object.keys(rows[0]);
  const hideOrgId = allCols.includes('organization_name') && allCols.includes('organization_id');
  const columns = hideOrgId
    ? allCols.filter((c) => c !== 'organization_id')
    : allCols;
  const fmtY = block?.chart_spec?.format?.y;
  const ySet = new Set((block?.chart_spec?.y_fields || []).map((y) => y.field));
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden"
      data-testid="report-block-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            {columns.map((c) => (
              <th key={c}
                className="text-left px-4 py-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                {c.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0">
              {columns.map((c) => (
                <td key={c} className="px-4 py-2 text-slate-700">
                  {typeof r[c] === 'number'
                    ? formatValue(r[c], ySet.has(c) ? fmtY : undefined)
                    : (r[c] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Pivot a long-format dataset into wide-format for stacked bars.
 *
 * Input  (long form, one row per (x, stack) pair, as produced by
 * the semantic layer's group_by=[x, stack]):
 *   [{org: "Seertech", pillar: "Engagement",   score: 71},
 *    {org: "Seertech", pillar: "Performance",  score: 78},
 *    {org: "Triton",   pillar: "Engagement",   score: 64}, ...]
 *
 * Output (wide form, one row per x, each stack becomes a column):
 *   [{org: "Seertech", Engagement: 71, Performance: 78, …},
 *    {org: "Triton",   Engagement: 64, …}]
 *
 * Why this is necessary: Recharts' <Bar dataKey=…> renders one
 * series per dataKey. To stack pillars on top of each other we
 * need one Bar per pillar, which requires one column per pillar.
 *
 * Returns ``{rows, stackKeys}`` so the caller knows which columns
 * to draw. Falls back to original rows when stackBy is empty.
 */
function pivotForStack(rows, xField, stackBy, valueField) {
  if (!stackBy || !rows?.length) return { rows, stackKeys: [] };
  const byX = new Map();
  const stackKeys = [];
  for (const r of rows) {
    const xRaw = r?.[xField];
    const xKey = (xRaw === null || xRaw === undefined || xRaw === '') ? 'Unknown' : xRaw;
    const sKey = r?.[stackBy];
    const sLabel = (sKey === null || sKey === undefined || sKey === '') ? 'Unknown' : String(sKey);
    if (!byX.has(xKey)) byX.set(xKey, { [xField]: xKey });
    const row = byX.get(xKey);
    const v = r?.[valueField];
    // Sum-by-default when there are duplicate (x,stack) pairs.
    row[sLabel] = (row[sLabel] || 0) + (typeof v === 'number' ? v : 0);
    if (!stackKeys.includes(sLabel)) stackKeys.push(sLabel);
  }
  // Stable column order — keep the first-seen order from input rows
  // so the legend reflects the data's natural ordering instead of
  // alphabetical surprises.
  return { rows: Array.from(byX.values()), stackKeys };
}

/**
 * Replace null / undefined / empty x-axis values with a readable
 * "Unknown" label. Recharts otherwise prints raw "undefined" text
 * directly on the axis, which looks broken — see user report
 * May 22, 2026.
 */
function coalesceXValues(rows, xField) {
  if (!xField || !rows?.length) return rows;
  let anyMissing = false;
  for (const r of rows) {
    const v = r?.[xField];
    if (v === null || v === undefined || v === '') { anyMissing = true; break; }
  }
  if (!anyMissing) return rows;
  return rows.map((r) => {
    const v = r?.[xField];
    if (v === null || v === undefined || v === '') {
      return { ...r, [xField]: 'Unknown' };
    }
    return r;
  });
}

function BarBlock({ block }) {
  let rows = block?.data?.rows || [];
  const x = pickXField(block)
    || (rows[0] ? Object.keys(rows[0]).find((k) => typeof rows[0][k] !== 'number') : 'name');
  const ys = ensureYFields(block);
  // Stacked-bar pivot. ``chart_spec.stack_by`` carries the inner
  // dimension (e.g. 'pillar_name'). When set, we pivot the long-form
  // rows into one column per stack value and render one Bar per
  // column with a shared stackId so Recharts stacks them.
  const stackBy = block?.chart_spec?.stack_by;
  const valueField = ys[0]?.field;
  let stackKeys = [];
  if (stackBy && valueField) {
    const pivoted = pivotForStack(rows, x, stackBy, valueField);
    rows = pivoted.rows;
    stackKeys = pivoted.stackKeys;
  }
  rows = coalesceXValues(rows, x);
  if (!stackBy && block?.chart_spec?.annotations?.trend_line && ys[0]) {
    rows = calcTrend(rows, x, ys[0].field) || rows;
  }
  const target = block?.chart_spec?.annotations?.target_line;
  const showLegend = block?.chart_spec?.legend !== false
    && (stackKeys.length > 1 || ys.length > 1);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4"
      data-testid="report-block-bar">
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={rows} margin={{ top: 10, right: 16, left: 0, bottom: 24 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={x} tick={{ fontSize: 11, fill: '#475569' }}
            angle={rows.length > 4 ? -18 : 0}
            textAnchor={rows.length > 4 ? 'end' : 'middle'} />
          <YAxis tick={{ fontSize: 11, fill: '#475569' }} />
          <RTooltip />
          {showLegend ? <Legend /> : null}
          {target ? (
            <ReferenceLine y={target.value} stroke="#ef4444" strokeDasharray="4 4"
              label={{ value: target.label || `Target ${target.value}`, fill: '#ef4444', fontSize: 11, position: 'top' }} />
          ) : null}
          {stackKeys.length > 0 ? (
            stackKeys.map((key, i) => (
              <Bar key={key} dataKey={key} name={key} stackId="a"
                fill={PALETTE[i % PALETTE.length]}
                radius={i === stackKeys.length - 1 ? [4, 4, 0, 0] : 0} />
            ))
          ) : (
            ys.map((y, i) => (
              <Bar key={y.field} dataKey={y.field} name={y.label || y.field}
                fill={y.color || PALETTE[i % PALETTE.length]} radius={[4, 4, 0, 0]} />
            ))
          )}
          {!stackBy && block?.chart_spec?.annotations?.trend_line && ys[0] ? (
            <Line type="monotone" dataKey="__trend" stroke="#64748b"
              strokeDasharray="3 3" dot={false} strokeWidth={1.5} name="Trend" />
          ) : null}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function LineBlock({ block }) {
  let rows = block?.data?.rows || [];
  const x = pickXField(block)
    || (rows[0] ? (rows[0].bucket !== undefined ? 'bucket' : Object.keys(rows[0])[0]) : 'date');
  const ys = ensureYFields(block);
  if (block?.chart_spec?.annotations?.trend_line && ys[0]) {
    rows = calcTrend(rows, x, ys[0].field) || rows;
  }
  const target = block?.chart_spec?.annotations?.target_line;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4"
      data-testid="report-block-line">
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={rows} margin={{ top: 10, right: 16, left: 0, bottom: 24 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={x} tick={{ fontSize: 11, fill: '#475569' }} />
          <YAxis tick={{ fontSize: 11, fill: '#475569' }} />
          <RTooltip />
          {block?.chart_spec?.legend && ys.length > 1 ? <Legend /> : null}
          {target ? (
            <ReferenceLine y={target.value} stroke="#ef4444" strokeDasharray="4 4"
              label={{ value: target.label || `Target ${target.value}`, fill: '#ef4444', fontSize: 11, position: 'top' }} />
          ) : null}
          {ys.map((y, i) => (
            <Line key={y.field} type="monotone" dataKey={y.field}
              name={y.label || y.field}
              stroke={y.color || PALETTE[i % PALETTE.length]}
              strokeWidth={2} dot={{ r: 3 }} />
          ))}
          {block?.chart_spec?.annotations?.trend_line && ys[0] ? (
            <Line type="monotone" dataKey="__trend" stroke="#64748b"
              strokeDasharray="3 3" dot={false} strokeWidth={1.5} name="Trend" />
          ) : null}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function AreaBlock({ block }) {
  const rows = block?.data?.rows || [];
  const x = pickXField(block)
    || (rows[0] ? Object.keys(rows[0])[0] : 'date');
  const ys = ensureYFields(block);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4"
      data-testid="report-block-area">
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={rows} margin={{ top: 10, right: 16, left: 0, bottom: 24 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey={x} tick={{ fontSize: 11, fill: '#475569' }} />
          <YAxis tick={{ fontSize: 11, fill: '#475569' }} />
          <RTooltip />
          {block?.chart_spec?.legend && ys.length > 1 ? <Legend /> : null}
          {ys.map((y, i) => (
            <Area key={y.field} type="monotone" dataKey={y.field}
              name={y.label || y.field}
              stroke={y.color || PALETTE[i % PALETTE.length]}
              fill={y.color || PALETTE[i % PALETTE.length]} fillOpacity={0.25} />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function PieBlock({ block }) {
  const rows = block?.data?.rows || [];
  const x = pickXField(block)
    || (rows[0] ? Object.keys(rows[0]).find((k) => typeof rows[0][k] !== 'number') : 'name');
  const y = block?.chart_spec?.y_fields?.[0]?.field
    || (rows[0] ? Object.keys(rows[0]).find((k) => typeof rows[0][k] === 'number') : 'value');
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4"
      data-testid="report-block-pie">
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie data={rows} dataKey={y} nameKey={x} cx="50%" cy="50%"
            outerRadius={90} label>
            {rows.map((_, i) => (
              <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
            ))}
          </Pie>
          <RTooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ReportBlockTile({ block, onAnnotationClick, onRefinePrompt }) {
  const ct = block?.chart_spec?.chart_type || 'table';
  if (block?.error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
        <strong>Block failed:</strong> {block.error}
      </div>
    );
  }
  let chart;
  if (ct === 'big_number') chart = <BigNumberBlock block={block} />;
  else if (ct === 'bar') chart = <BarBlock block={block} />;
  else if (ct === 'line') chart = <LineBlock block={block} />;
  else if (ct === 'area') chart = <AreaBlock block={block} />;
  else if (ct === 'pie') chart = <PieBlock block={block} />;
  else chart = <TableBlock block={block} />;

  // ── Inline narrative annotations ────────────────────────────
  // The backend's annotation pass attaches 0-3 short callouts per
  // block: warnings (red), celebrations (green), or neutral insights
  // (indigo). Each carries an optional follow-up ``prompt`` that —
  // when clicked — feeds straight into the next /turn call, turning
  // a static chart into a guided conversation entry point.
  const annotations = block?.annotations || [];

  // "What did Claude pick?" disclosure — collapsed by default, opens
  // a compact summary of the LLM's planning decision (entity, group_by,
  // measures, filters, having) so admins can debug routing at a glance.
  // The Visual Editor plugin instrumenting JSX in dev strips
  // function-typed props it doesn't recognise (everything except
  // onClick/onChange/etc) — so we smuggle the refine callback through
  // the ``block`` prop (which is an object the plugin lets through
  // untouched) rather than expose it as a top-level prop.
  const blockWithRefine = onRefinePrompt
    ? { ...block, __onRefinePrompt: onRefinePrompt }
    : block;
  const disclosure = <ClaudePickDisclosure block={blockWithRefine} />;

  return (
    <div className="space-y-2.5">
      {chart}
      {annotations.length ? (
        <ul className="space-y-1.5" data-testid={`block-annotations-${block?.id || 'unknown'}`}>
          {annotations.map((a, i) => {
            const tone = {
              warning:     { border: 'border-rose-200',    bg: 'bg-rose-50',    text: 'text-rose-900',    chip: 'text-rose-700 hover:bg-rose-100',     Icon: AlertTriangle,  iconClass: 'text-rose-600' },
              celebration: { border: 'border-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-900', chip: 'text-emerald-700 hover:bg-emerald-100', Icon: TrendingUp,     iconClass: 'text-emerald-600' },
              insight:     { border: 'border-indigo-200',  bg: 'bg-indigo-50',  text: 'text-indigo-900',  chip: 'text-indigo-700 hover:bg-indigo-100',   Icon: Sparkles,       iconClass: 'text-indigo-600' },
            }[a.kind] || {
              border: 'border-slate-200', bg: 'bg-slate-50', text: 'text-slate-900',
              chip: 'text-slate-700 hover:bg-slate-100', Icon: Sparkles, iconClass: 'text-slate-500',
            };
            const { Icon } = tone;
            return (
              <li
                key={i}
                className={`rounded-md border ${tone.border} ${tone.bg} px-3 py-2 flex items-start gap-2 text-xs ${tone.text}`}
                data-testid={`annotation-${block?.id}-${i}`}
              >
                <Icon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${tone.iconClass}`} />
                <div className="flex-1 min-w-0">
                  <div className="leading-snug">
                    {a.target ? <span className="font-semibold">{a.target}: </span> : null}
                    {a.text}
                  </div>
                  {a.prompt && onAnnotationClick ? (
                    <button
                      type="button"
                      onClick={() => onAnnotationClick(a.prompt)}
                      className={`mt-1 text-[11px] font-medium inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/60 ${tone.chip} transition-colors`}
                      data-testid={`annotation-prompt-${block?.id}-${i}`}
                    >
                      <Sparkles className="h-2.5 w-2.5" />
                      {a.prompt}
                    </button>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
      {disclosure}
    </div>
  );
}

// ─── "What did Claude pick?" disclosure ─────────────────────────
/**
 * Collapsed accordion summarising the LLM's planning decision for
 * THIS block: entity, group_by, measures, filters, having, sort,
 * time_grain, and the resolved chart_spec essentials. Helps admins
 * debug "why does this chart look off?" without opening the network
 * tab — confirm at a glance that Claude understood the question
 * (right entity? right grouping? right measure?).
 *
 * Rendered subtle by default — a tiny "What did Claude pick?" link.
 * Only expands on click so the chart stays the visual headline. Uses
 * a native <details> element for zero-JS toggle + automatic keyboard
 * accessibility (Space / Enter to expand).
 *
 * Interactive plan editing
 * ────────────────────────
 * When the parent supplies ``onRefinePrompt``, every row in the
 * expanded view becomes a one-click refine action that drops a
 * sensible follow-up prompt into the chat input. Examples:
 *   • Click entity → "Show me what other entities I could pick instead"
 *   • Click a filter → "Remove the filter on <field>"
 *   • Click a having row → "Remove the threshold on <field>"
 *   • Click a measure → "Switch the measure to count"
 *   • Click chart row → "Switch this to a line chart"
 * Without the callback (e.g. on pinned tiles where there's no chat
 * to refine into), rows render as inert text — the disclosure still
 * works as a read-only debug strip.
 */
function ClaudePickDisclosure({ block }) {
  // The refine callback is smuggled in via the block object so the
  // dev-time Visual Editor JSX instrumentation doesn't strip it
  // (see ReportBlockTile for the full explanation).
  const onRefinePrompt = block?.__onRefinePrompt;
  const qs = block?.query_spec;
  const cs = block?.chart_spec;
  if (!qs && !cs) return null;
  const measures = qs?.measures || [];
  const filters = qs?.filters || [];
  const having = qs?.having || [];
  const groupBy = qs?.group_by || [];
  const sort = qs?.sort || [];

  const fmtFilter = (f) => {
    const val = Array.isArray(f.value) ? `[${f.value.length}]` : JSON.stringify(f.value);
    return `${f.field} ${f.op} ${val}`;
  };

  // Each row carries an optional ``refine`` prompt that the parent
  // can drop into the chat input on click. We pass undefined when
  // no callback is present so rows render as plain text.
  const refineFor = (kind, payload) => {
    if (!onRefinePrompt) return undefined;
    switch (kind) {
      case 'entity':
        return `Switch this chart to use a different entity than ${payload}`;
      case 'group_by':
        return `Stop grouping by ${payload}`;
      case 'measure':
        return `Change the measure on this chart`;
      case 'filter':
        return `Remove the filter on ${payload}`;
      case 'having':
        return `Remove the ${payload} threshold from this chart`;
      case 'sort':
        return `Sort this chart by a different field`;
      case 'limit':
        return `Show more than ${payload} rows`;
      case 'chart':
        return `Switch this to a line chart`;
      default:
        return undefined;
    }
  };

  return (
    <details
      className="rounded-md border border-slate-200 bg-slate-50/60 text-xs"
      data-testid={`block-claude-pick-${block?.id || 'unknown'}`}
    >
      <summary className="px-3 py-1.5 cursor-pointer text-slate-600 hover:text-slate-900 flex items-center gap-1.5 select-none">
        <Search className="h-3 w-3" />
        <span className="font-medium">What did Claude pick?</span>
        <span className="text-slate-400 font-mono">
          {qs?.entity || '—'}{groupBy.length ? ` · group_by=${groupBy.length}` : ''}
        </span>
        {onRefinePrompt ? (
          <span className="ml-auto text-[10px] text-slate-400 font-normal italic">
            click rows to refine
          </span>
        ) : null}
      </summary>
      <div className="px-3 pb-3 pt-1 space-y-1.5 font-mono text-[11px] text-slate-700 leading-snug">
        {qs?.entity ? (
          <Row label="entity" value={qs.entity} onClick={onRefinePrompt && (() => onRefinePrompt(refineFor('entity', qs.entity)))} />
        ) : null}
        {groupBy.length ? (
          groupBy.map((g) => (
            <Row key={g} label="group_by" value={g}
              onClick={onRefinePrompt && (() => onRefinePrompt(refineFor('group_by', g)))} />
          ))
        ) : null}
        {measures.length ? (
          <Row
            label="measures"
            value={measures.map((m) => (m.as ? `${m.id} as ${m.as}` : m.id)).join(' · ')}
            onClick={onRefinePrompt && (() => onRefinePrompt(refineFor('measure')))}
          />
        ) : null}
        {filters.length ? (
          filters.map((f, i) => (
            <Row key={`f-${i}`} label="filters" value={fmtFilter(f)}
              onClick={onRefinePrompt && (() => onRefinePrompt(refineFor('filter', f.field)))} />
          ))
        ) : (
          <Row label="filters" value={<span className="italic text-slate-400">none</span>} />
        )}
        {having.length ? (
          having.map((h, i) => (
            <Row key={`h-${i}`} label="having" value={fmtFilter(h)} highlight
              onClick={onRefinePrompt && (() => onRefinePrompt(refineFor('having', h.field)))} />
          ))
        ) : null}
        {qs?.time_grain ? (
          <Row label="time_grain" value={qs.time_grain} />
        ) : null}
        {sort.length ? (
          <Row
            label="sort"
            value={sort.map((s) => `${s.field} ${s.direction || 'asc'}`).join(' · ')}
            onClick={onRefinePrompt && (() => onRefinePrompt(refineFor('sort')))}
          />
        ) : null}
        {qs?.limit ? (
          <Row label="limit" value={String(qs.limit)}
            onClick={onRefinePrompt && (() => onRefinePrompt(refineFor('limit', qs.limit)))} />
        ) : null}
        {cs?.chart_type ? (
          <Row
            label="chart"
            value={`${cs.chart_type}${cs.stack_by ? ` · stacked by ${cs.stack_by}` : ''}${cs.x_field ? ` · x=${cs.x_field}` : ''}`}
            onClick={onRefinePrompt && (() => onRefinePrompt(refineFor('chart')))}
          />
        ) : null}
      </div>
    </details>
  );
}

function Row({ label, value, highlight, onClick }) {
  const interactive = typeof onClick === 'function';
  const baseClass = `flex items-baseline gap-2 ${interactive ? 'cursor-pointer rounded px-1 -mx-1 hover:bg-white hover:shadow-sm transition-colors' : ''}`;
  return (
    <div
      className={baseClass}
      onClick={interactive ? onClick : undefined}
      onKeyDown={interactive ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      } : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      title={interactive ? 'Click to refine this in the chat' : undefined}
      data-testid={`claude-pick-row-${label}`}
    >
      <span className="text-slate-400 w-[78px] flex-shrink-0">{label}:</span>
      <span className={`flex-1 break-all ${highlight ? 'text-indigo-700 font-semibold' : ''}`}>
        {value}
      </span>
    </div>
  );
}

export default ReportBlockTile;
